import { useCallback, useMemo, useState } from "react";

interface WizardConfig<T extends string = string> {
  steps: readonly T[];
  canNext?: (stepIndex: number, currentStep: T) => boolean;
  canPrev?: (stepIndex: number, currentStep: T) => boolean;
  onStepChange?: (stepIndex: number, currentStep: T) => void;
  initialStep?: number;
}

interface UseWizardReturn<T extends string = string> {
  currentStep: T;
  currentStepIndex: number;
  totalSteps: number;
  steps: readonly T[];
  canNext: boolean;
  canPrev: boolean;
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  prev: () => void;
  goTo: (stepIndex: number) => void;
  reset: () => void;
}

export const useWizard = <T extends string = string>(
  config: WizardConfig<T>,
): UseWizardReturn<T> => {
  const {
    steps,
    canNext: canNextFn,
    canPrev: canPrevFn,
    onStepChange,
    initialStep = 0,
  } = config;
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);

  const currentStep = steps[currentStepIndex];
  const isFirst = currentStepIndex === 0;
  const isLast = currentStepIndex === steps.length - 1;

  const canNext = useMemo(() => {
    if (isLast) return false;
    if (canNextFn) {
      return canNextFn(currentStepIndex, currentStep);
    }
    return true;
  }, [currentStepIndex, currentStep, isLast, canNextFn]);

  const canPrev = useMemo(() => {
    if (isFirst) return false;
    if (canPrevFn) {
      return canPrevFn(currentStepIndex, currentStep);
    }
    return true;
  }, [currentStepIndex, currentStep, isFirst, canPrevFn]);

  const next = useCallback(() => {
    if (canNext && !isLast) {
      const newIndex = currentStepIndex + 1;
      setCurrentStepIndex(newIndex);
      onStepChange?.(newIndex, steps[newIndex]);
    }
  }, [canNext, isLast, currentStepIndex, steps, onStepChange]);

  const prev = useCallback(() => {
    if (canPrev && !isFirst) {
      const newIndex = currentStepIndex - 1;
      setCurrentStepIndex(newIndex);
      onStepChange?.(newIndex, steps[newIndex]);
    }
  }, [canPrev, isFirst, currentStepIndex, steps, onStepChange]);

  const goTo = useCallback(
    (stepIndex: number) => {
      if (stepIndex >= 0 && stepIndex < steps.length) {
        setCurrentStepIndex(stepIndex);
        onStepChange?.(stepIndex, steps[stepIndex]);
      }
    },
    [steps, onStepChange],
  );

  const reset = useCallback(() => {
    setCurrentStepIndex(initialStep);
    onStepChange?.(initialStep, steps[initialStep]);
  }, [initialStep, steps, onStepChange]);

  return {
    currentStep,
    currentStepIndex,
    totalSteps: steps.length,
    steps,
    canNext,
    canPrev,
    isFirst,
    isLast,
    next,
    prev,
    goTo,
    reset,
  };
};
